// UI string dictionaries — one per locale. `en` is the source of truth: its
// keys define `MessageKey`, and every other locale must provide all of them
// (enforced by the `Messages` type). Placeholders use `{name}` and are filled
// by `translate()`.
//
// Scope: the game top bar, its menus, the profile view and the milestone
// cinematics (plus their Gameplay setting). The setup screen keeps its
// strings in ../setup/messages.ts and the in-game HUD (event log, panels,
// combat overlay, settings + help modals) in ./game.ts; both are spread in here.

import { setupEn, setupEs } from '../setup/messages.js';
import { gameEn, gameEs, gameHelpEn, gameHelpEs } from './game.js';
import { gameDynastyEn } from './gameDynastyEn.js';
import { gameDynastyEs } from './gameDynastyEs.js';
import { gameEspionageEn } from './gameEspionageEn.js';
import { gameEspionageEs } from './gameEspionageEs.js';
import { gameHeroesEn } from './gameHeroesEn.js';
import { gameHeroesEs } from './gameHeroesEs.js';
import { gameLayoutEn } from './gameLayoutEn.js';
import { gameLayoutEs } from './gameLayoutEs.js';
import { gameLegendsEn } from './gameLegendsEn.js';
import { gameLegendsEs } from './gameLegendsEs.js';
import { gameLegendsKindsEn } from './gameLegendsKindsEn.js';
import { gameLegendsKindsEs } from './gameLegendsKindsEs.js';
import { gameMilestonesEn } from './gameMilestonesEn.js';
import { gameMilestonesEs } from './gameMilestonesEs.js';
import { gameOpNewsEn } from './gameOpNewsEn.js';
import { gameOpNewsEs } from './gameOpNewsEs.js';
import { gameResearchEn } from './gameResearchEn.js';
import { gameResearchEs } from './gameResearchEs.js';
import { gameTreatyEn } from './gameTreatyEn.js';
import { gameTreatyEs } from './gameTreatyEs.js';
import { minimapEn } from './minimapEn.js';
import { minimapEs } from './minimapEs.js';

export const en = {
  'app.title': 'Ages of Dominion',
  'app.logoAlt': 'Ages of Dominion logo',

  'menu.game': 'Game',
  'menu.game.newMatch': 'New match',
  'menu.game.save': 'Save game',
  'menu.game.load': 'Load game',
  'menu.game.mainMenu': 'Return to main menu',
  'menu.help': 'Help',
  'menu.help.hints': 'Hint topics',
  'menu.help.reference': 'Reference help',

  'resources.label': 'Resources',
  'resources.research': 'Research ({cost})',
  'resources.researchActive': 'Research: {ticks} ticks',
  'resources.researchTitle': 'Spend 150 knowledge for 8 turns of +50% industry, +30% knowledge',
  'resources.crisis': 'Crisis: {tension}%',
  'resources.crisisActions': 'Ease the faith crisis',
  'resources.pilgrimage': 'Pilgrimage',
  'resources.pilgrimageTitle': 'Spend 100 gold to reduce faith crisis tension by 20',
  'resources.reform': 'Reform',
  'resources.reformTitle': 'Spend 50 faith to reduce faith crisis tension by 15',

  'time.label': 'Time controls',
  'time.pause': 'Pause simulation',
  'time.resume': 'Resume simulation',
  'time.setSpeed': 'Set speed to {speed}×',
  'time.cycleSpeed': 'Game speed {speed}× — click to change',

  'theme.label': 'Theme',
  'theme.switch': 'Switch to {theme} theme',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'theme.system': 'System',

  'locale.label': 'Language',

  'settings.open': 'Settings',

  'hud.doctrine': 'Doctrine',
  'hud.map': 'Map',
  'hud.info': 'Info',

  'auth.signIn': 'Sign in',
  'auth.signOut': 'Sign out',
  'auth.profile': 'Profile',
  'auth.account': 'Account menu',
  'auth.unavailable': 'Online accounts are not available in this build',

  'profile.title': 'Profile',
  'profile.name': 'Name',
  'profile.email': 'Email',
  'profile.unnamed': 'Unnamed player',
  'profile.close': 'Close',

  ...setupEn,
  'settings.cinematics': 'Cinematics',
  'settings.cinematics.description': 'A short camera moment when your realm reaches a milestone',
  'settings.cinematics.all': 'All',
  'settings.cinematics.big': 'Big moments',
  'settings.cinematics.off': 'Off',

  'cinematic.label': 'Milestone',
  'cinematic.skip': 'Skip',
  'cinematic.skipHint': 'Press any key to skip',
  'cinematic.toastLabel': 'Milestone reached',
  'cinematic.cityFallback': 'Your city',
  'cinematic.heroFallback': 'Your hero',
  // Graduation new-rule moment (rulesets spec §10 / R8).
  'cinematic.graduation.title': 'New rule: {name}',
  'cinematic.graduation.subtitle': 'A new rule joins this match',
  'cinematic.graduation.kid': 'New toy: {name}!',
  'cinematic.playerName': 'Player {n}',

  'cinematic.tierUp.2.title': '{name} becomes a Town',
  'cinematic.tierUp.3.title': '{name} becomes a City',
  'cinematic.tierUp.4.title': '{name} becomes a Metropolis',
  'cinematic.tierUp.title': '{name} grows',
  'cinematic.tierUp.2.subtitle': 'New homes and workshops rise along its lanes.',
  'cinematic.tierUp.3.subtitle': 'Markets and walls crown its hills.',
  'cinematic.tierUp.4.subtitle': 'Its towers can be seen across the realm.',
  'cinematic.tierUp.subtitle': 'Your people prosper.',
  'cinematic.tierUp.2.kid': 'Your village grew into a town!',
  'cinematic.tierUp.3.kid': 'Your town grew into a city!',
  'cinematic.tierUp.4.kid': 'Your city grew into a giant metropolis!',
  'cinematic.tierUp.kid': 'Your city grew bigger!',

  'cinematic.firstFounded.title': 'A new city: {name}',
  'cinematic.firstFounded.subtitle': 'Your realm reaches beyond its capital.',
  'cinematic.firstFounded.kid': 'You built a new city!',
  'cinematic.firstCapture.title': '{name} is yours',
  'cinematic.firstCapture.subtitle': 'Your first conquest.',
  'cinematic.firstCapture.kid': 'You won your first city!',
  'cinematic.capitalCaptured.title': 'The capital {name} has fallen',
  'cinematic.capitalCaptured.subtitle': 'An enemy realm is broken.',
  'cinematic.capitalCaptured.kid': "You took the enemy's biggest city!",
  'cinematic.heroRecruited.title': 'Your first hero: {name}',
  'cinematic.heroRecruited.subtitle': 'Legends begin with a single step.',
  'cinematic.heroRecruited.kid': 'A hero joined you!',
  'cinematic.heroLevel.title': '{name} reaches level {level}',
  'cinematic.heroLevel.subtitle': 'Tales of their deeds spread.',
  'cinematic.heroLevel.kid': 'Your hero got stronger!',
  'cinematic.doctrine.title': 'Doctrine tier {tier}',
  'cinematic.doctrine.subtitle': 'Your realm masters new ways.',
  'cinematic.doctrine.kid': 'You learned something new!',
  'cinematic.temple.title': 'Your first temple',
  'cinematic.temple.subtitle': 'The faithful gather.',
  'cinematic.temple.kid': 'You built a temple!',
  'cinematic.wonder.title': 'A wonder rises: {name}',
  'cinematic.wonder.subtitle': 'It will be remembered for ages.',
  'cinematic.wonder.kid': 'You built a wonder!',
  'cinematic.merchantRoute.title': 'A house at your harbour',
  'cinematic.merchantRoute.subtitle':
    'A foreign house is trading with you — gold and grain every tick.',
  'cinematic.merchantRoute.kid': 'Someone wants to do business!',
  'cinematic.ritual.title': 'The ritual is complete',
  'cinematic.ritual.subtitle': 'Old powers answer your call.',
  'cinematic.ritual.kid': 'Your magic worked!',
  'cinematic.peace.title': 'Peace with {player}',
  'cinematic.peace.subtitle': 'The banners are lowered.',
  'cinematic.peace.kid': 'You made peace!',
  'cinematic.battle.title': 'A decisive battle',
  'cinematic.battle.subtitle': 'The field is yours.',
  'cinematic.battle.kid': 'You won a big battle!',
  'cinematic.era.title': 'A new era begins',
  'cinematic.era.subtitle': 'Era {era} of your realm.',
  'cinematic.era.kid': 'Your empire grew up!',
  'cinematic.legendSummoned.title': 'A Champion rises',
  'cinematic.legendSummoned.subtitle': 'A legend answers your call.',
  'cinematic.legendSummoned.kid': 'A giant dragon joined you!',
  'cinematic.legendFallen.title': 'A Champion has fallen',
  'cinematic.legendFallen.subtitle': 'Its legend will return.',
  'cinematic.legendFallen.kid': 'Your dragon fell down!',
  'cinematic.victory.title': 'Victory!',
  'cinematic.victory.conquest': 'The whole realm bows to you.',
  'cinematic.victory.economy': 'Your gold and trade rule the realm.',
  'cinematic.victory.science': 'Your scholars have unlocked the secrets of the age.',
  'cinematic.victory.faith': 'Your faith has united the world.',
  'cinematic.victory.diplomacy': 'Your alliances have brought lasting peace.',
  'cinematic.victory.subtitle': 'Your name will be remembered.',
  'cinematic.victory.kid': 'You won the game! Well done!',
  'cinematic.defeat.title': '{player} is victorious',
  'cinematic.defeat.subtitle': 'Your realm lives on in the chronicles.',
  'cinematic.defeat.kid': 'Good try! Want to play again?',
  'cinematic.montage.title': 'Across your realm',
  'cinematic.montage.more': '+{count} more',
  'cinematic.montage.kid': 'Lots of good things happened!',
  ...gameEn,
  ...gameHelpEn,
  ...gameDynastyEn,
  ...gameResearchEn,
  ...gameEspionageEn,
  ...gameHeroesEn,
  ...gameLegendsEn,
  ...gameMilestonesEn,
  ...gameTreatyEn,
  ...gameOpNewsEn,
  ...gameLegendsKindsEn,
  ...minimapEn,
  ...gameLayoutEn,
} as const;

export type MessageKey = keyof typeof en;
export type Messages = Record<MessageKey, string>;

export const es: Messages = {
  'app.title': 'Ages of Dominion',
  'app.logoAlt': 'Logotipo de Ages of Dominion',

  'menu.game': 'Partida',
  'menu.game.newMatch': 'Nueva partida',
  'menu.game.save': 'Guardar partida',
  'menu.game.load': 'Cargar partida',
  'menu.game.mainMenu': 'Volver al menú principal',
  'menu.help': 'Ayuda',
  'menu.help.hints': 'Temas de ayuda',
  'menu.help.reference': 'Guía de referencia',

  'resources.label': 'Recursos',
  'resources.research': 'Investigar ({cost})',
  'resources.researchActive': 'Investigación: {ticks} ticks',
  'resources.researchTitle':
    'Gasta 150 de conocimiento para 8 turnos de +50 % de industria y +30 % de conocimiento',
  'resources.crisis': 'Crisis: {tension} %',
  'resources.crisisActions': 'Calmar la crisis de fe',
  'resources.pilgrimage': 'Peregrinación',
  'resources.pilgrimageTitle': 'Gasta 100 de oro para reducir la tensión de la crisis de fe en 20',
  'resources.reform': 'Reforma',
  'resources.reformTitle': 'Gasta 50 de fe para reducir la tensión de la crisis de fe en 15',

  'time.label': 'Control del tiempo',
  'time.pause': 'Pausar simulación',
  'time.resume': 'Reanudar simulación',
  'time.setSpeed': 'Velocidad {speed}×',
  'time.cycleSpeed': 'Velocidad {speed}× — pulsa para cambiar',

  'theme.label': 'Tema',
  'theme.switch': 'Cambiar a tema {theme}',
  'theme.light': 'Claro',
  'theme.dark': 'Oscuro',
  'theme.system': 'Sistema',

  'locale.label': 'Idioma',

  'settings.open': 'Ajustes',

  'hud.doctrine': 'Doctrina',
  'hud.map': 'Mapa',
  'hud.info': 'Info',

  'auth.signIn': 'Iniciar sesión',
  'auth.signOut': 'Cerrar sesión',
  'auth.profile': 'Perfil',
  'auth.account': 'Menú de la cuenta',
  'auth.unavailable': 'Las cuentas en línea no están disponibles en esta versión',

  'profile.title': 'Perfil',
  'profile.name': 'Nombre',
  'profile.email': 'Correo electrónico',
  'profile.unnamed': 'Jugador sin nombre',
  'profile.close': 'Cerrar',

  ...setupEs,
  'settings.cinematics': 'Cinemáticas',
  'settings.cinematics.description': 'Un breve momento de cámara cuando tu reino alcanza un hito',
  'settings.cinematics.all': 'Todas',
  'settings.cinematics.big': 'Grandes momentos',
  'settings.cinematics.off': 'No',

  'cinematic.label': 'Hito',
  'cinematic.skip': 'Saltar',
  'cinematic.skipHint': 'Pulsa cualquier tecla para saltar',
  'cinematic.toastLabel': 'Hito alcanzado',
  'cinematic.cityFallback': 'Tu ciudad',
  'cinematic.heroFallback': 'Tu héroe',
  // Graduation new-rule moment (rulesets spec §10 / R8).
  'cinematic.graduation.title': 'Nueva regla: {name}',
  'cinematic.graduation.subtitle': 'Una regla nueva se une a esta partida',
  'cinematic.graduation.kid': '¡Juguete nuevo: {name}!',
  'cinematic.playerName': 'Jugador {n}',

  'cinematic.tierUp.2.title': '{name} se convierte en villa',
  'cinematic.tierUp.3.title': '{name} se convierte en ciudad',
  'cinematic.tierUp.4.title': '{name} se convierte en metrópolis',
  'cinematic.tierUp.title': '{name} crece',
  'cinematic.tierUp.2.subtitle': 'Nuevas casas y talleres se alzan en sus calles.',
  'cinematic.tierUp.3.subtitle': 'Mercados y murallas coronan sus colinas.',
  'cinematic.tierUp.4.subtitle': 'Sus torres se ven desde todo el reino.',
  'cinematic.tierUp.subtitle': 'Tu pueblo prospera.',
  'cinematic.tierUp.2.kid': '¡Tu aldea se ha convertido en una villa!',
  'cinematic.tierUp.3.kid': '¡Tu villa se ha convertido en una ciudad!',
  'cinematic.tierUp.4.kid': '¡Tu ciudad se ha convertido en una gran metrópolis!',
  'cinematic.tierUp.kid': '¡Tu ciudad ha crecido!',

  'cinematic.firstFounded.title': 'Una nueva ciudad: {name}',
  'cinematic.firstFounded.subtitle': 'Tu reino llega más allá de su capital.',
  'cinematic.firstFounded.kid': '¡Has fundado una ciudad nueva!',
  'cinematic.firstCapture.title': '{name} es tuya',
  'cinematic.firstCapture.subtitle': 'Tu primera conquista.',
  'cinematic.firstCapture.kid': '¡Has ganado tu primera ciudad!',
  'cinematic.capitalCaptured.title': 'Ha caído la capital {name}',
  'cinematic.capitalCaptured.subtitle': 'Un reino enemigo se derrumba.',
  'cinematic.capitalCaptured.kid': '¡Has tomado la ciudad más grande del enemigo!',
  'cinematic.heroRecruited.title': 'Tu primer héroe: {name}',
  'cinematic.heroRecruited.subtitle': 'Toda leyenda empieza con un paso.',
  'cinematic.heroRecruited.kid': '¡Un héroe se ha unido a ti!',
  'cinematic.heroLevel.title': '{name} alcanza el nivel {level}',
  'cinematic.heroLevel.subtitle': 'Las historias de sus hazañas se extienden.',
  'cinematic.heroLevel.kid': '¡Tu héroe es más fuerte!',
  'cinematic.doctrine.title': 'Doctrina de nivel {tier}',
  'cinematic.doctrine.subtitle': 'Tu reino domina nuevos caminos.',
  'cinematic.doctrine.kid': '¡Has aprendido algo nuevo!',
  'cinematic.temple.title': 'Tu primer templo',
  'cinematic.temple.subtitle': 'Los fieles se reúnen.',
  'cinematic.temple.kid': '¡Has construido un templo!',
  'cinematic.wonder.title': 'Se alza una maravilla: {name}',
  'cinematic.wonder.subtitle': 'Será recordada durante siglos.',
  'cinematic.wonder.kid': '¡Has construido una maravilla!',
  'cinematic.merchantRoute.title': 'Una casa en tu puerto',
  'cinematic.merchantRoute.subtitle':
    'Una casa extranjera comercia contigo: oro y grano en cada ticada.',
  'cinematic.merchantRoute.kid': '¡Alguien quiere hacer negocios!',
  'cinematic.ritual.title': 'El ritual se ha completado',
  'cinematic.ritual.subtitle': 'Antiguos poderes responden a tu llamada.',
  'cinematic.ritual.kid': '¡Tu magia ha funcionado!',
  'cinematic.peace.title': 'Paz con {player}',
  'cinematic.peace.subtitle': 'Se bajan los estandartes.',
  'cinematic.peace.kid': '¡Has hecho las paces!',
  'cinematic.battle.title': 'Una batalla decisiva',
  'cinematic.battle.subtitle': 'El campo es tuyo.',
  'cinematic.battle.kid': '¡Has ganado una gran batalla!',
  'cinematic.era.title': 'Comienza una nueva era',
  'cinematic.era.subtitle': 'Era {era} de tu reino.',
  'cinematic.era.kid': '¡Tu imperio ha crecido!',
  'cinematic.legendSummoned.title': 'Surge un Campeón',
  'cinematic.legendSummoned.subtitle': 'Una leyenda responde a tu llamada.',
  'cinematic.legendSummoned.kid': '¡Un dragón gigante se ha unido a ti!',
  'cinematic.legendFallen.title': 'Un Campeón ha caído',
  'cinematic.legendFallen.subtitle': 'Su leyenda volverá.',
  'cinematic.legendFallen.kid': '¡Tu dragón se ha caído!',
  'cinematic.victory.title': '¡Victoria!',
  'cinematic.victory.conquest': 'Todo el reino se inclina ante ti.',
  'cinematic.victory.economy': 'Tu oro y tu comercio gobiernan el reino.',
  'cinematic.victory.science': 'Tus sabios han desvelado los secretos de la era.',
  'cinematic.victory.faith': 'Tu fe ha unido al mundo.',
  'cinematic.victory.diplomacy': 'Tus alianzas han traído una paz duradera.',
  'cinematic.victory.subtitle': 'Tu nombre será recordado.',
  'cinematic.victory.kid': '¡Has ganado la partida! ¡Muy bien!',
  'cinematic.defeat.title': '{player} ha vencido',
  'cinematic.defeat.subtitle': 'Tu reino vive en las crónicas.',
  'cinematic.defeat.kid': '¡Buen intento! ¿Jugamos otra vez?',
  'cinematic.montage.title': 'En todo tu reino',
  'cinematic.montage.more': '+{count} más',
  'cinematic.montage.kid': '¡Han pasado muchas cosas buenas!',
  ...gameEs,
  ...gameHelpEs,
  ...gameDynastyEs,
  ...gameResearchEs,
  ...gameEspionageEs,
  ...gameHeroesEs,
  ...gameLegendsEs,
  ...gameMilestonesEs,
  ...gameTreatyEs,
  ...gameOpNewsEs,
  ...gameLegendsKindsEs,
  ...minimapEs,
  ...gameLayoutEs,
};
